/**
 * Scroll / viewport reveal for pym-embedded iframes (viewport-iframe-position) with
 * IntersectionObserver fallback when not in an iframe (local dev).
 *
 * Parent payload (space-separated floats): viewport width, viewport height, iframe top, …
 * Child: tp = el.getBoundingClientRect().top; guard tp > 0;
 * tpos = 1 - ((tp + positions[2]) / positions[1]); inview when tpos > threshold.
 */

function isInIframe() {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
}

/**
 * @param {object} opts
 * @param {import('pym.js').Child | null} opts.pymChild
 * @param {() => HTMLElement | null | undefined} opts.getTrackedElement
 * @param {(state: { inview: boolean; fadeArmed: boolean }) => void} opts.onState
 * @param {number} [opts.threshold=0.1]
 * @returns {() => void} cleanup
 */
export function setupViewportInview({ pymChild, getTrackedElement, onState, threshold = 0.1 }) {
    let positions = null;
    let intervalId = null;
    let io = null;

    function applyPym() {
        const el = getTrackedElement();
        if (!el || !positions || positions.length < 3) return;
        const vh = positions[1];
        if (!Number.isFinite(vh) || vh === 0) return;
        const tp = el.getBoundingClientRect().top;
        if (tp <= 0) return;
        const tpos = 1 - (tp + positions[2]) / vh;
        const inview = tpos > threshold;
        onState({ inview, fadeArmed: true });
    }

    function onViewportMessage(parentInfo) {
        positions = String(parentInfo)
            .trim()
            .split(/\s+/)
            .map(parseFloat);
        applyPym();
    }

    function cleanup() {
        if (intervalId != null) {
            clearInterval(intervalId);
            intervalId = null;
        }
        if (io) {
            io.disconnect();
            io = null;
        }
    }

    if (isInIframe() && pymChild && typeof pymChild.onMessage === 'function') {
        pymChild.onMessage('viewport-iframe-position', onViewportMessage);
        intervalId = setInterval(() => {
            if (typeof pymChild.getParentPositionInfo === 'function') {
                pymChild.getParentPositionInfo();
            }
        }, 1500);
        if (typeof pymChild.getParentPositionInfo === 'function') {
            pymChild.getParentPositionInfo();
        }
    } else {
        let retries = 0;
        const tryObserve = () => {
            const el = getTrackedElement();
            if (!el && retries < 90) {
                retries += 1;
                requestAnimationFrame(tryObserve);
                return;
            }
            if (!el || typeof IntersectionObserver === 'undefined') {
                onState({ inview: true, fadeArmed: true });
                return;
            }
            io = new IntersectionObserver(
                (entries) => {
                    const e = entries[0];
                    const inview = e.isIntersecting && e.intersectionRatio >= threshold;
                    onState({ inview, fadeArmed: true });
                },
                { threshold: [0, 0.1, 0.15, 0.2, 0.25, 0.33, 0.5, 0.75, 1] },
            );
            io.observe(el);
        };
        requestAnimationFrame(tryObserve);
    }

    return cleanup;
}
